/**
 * CaptureGate — the reversible half of push-to-talk.
 *
 * A turn cannot be opened speculatively (TURN_CHANGE interrupts the tutor,
 * DISCUSS_CONTEXT submits work), but students speak the instant they press.
 * So capture starts at key-down and buffers here, while the irreversible turn
 * open waits for the threshold. A tap discards the buffer and nothing ever
 * left the browser.
 *
 *   idle  — no capture; chunks dropped (stale flushes after an abort).
 *   armed — worklet running, chunks buffered locally, nothing emitted.
 *   live  — a turn is open; chunks pass straight through.
 */

export const GATE_IDLE = "idle";
export const GATE_ARMED = "armed";
export const GATE_LIVE = "live";

// The arm window is one tap threshold (~3 chunks). This cap only matters if an
// arm leaks, bounding memory until the safety-net abort runs — deliberately
// loose so raising the threshold never requires touching it.
const MAX_BUFFERED_CHUNKS = 50;

export class CaptureGate {
  constructor() {
    this.state = GATE_IDLE;
    this._buffer = [];
    this.droppedWhileArmed = 0;
  }

  /** Begin buffering. Any previously buffered audio is discarded. */
  arm() {
    this.state = GATE_ARMED;
    this._buffer = [];
    this.droppedWhileArmed = 0;
  }

  /**
   * Flush the buffer in order, then pass chunks straight through. Call *after*
   * TURN_CHANGE is emitted so flushed chunks land inside the new turn. Safe
   * from idle (the mouse path never arms) — it just goes live.
   *
   * @returns {number} how many buffered chunks were flushed
   */
  commit(emitChunk) {
    const buffered = this._buffer;
    this._buffer = [];
    this.state = GATE_LIVE;
    for (const msg of buffered) emitChunk(msg);
    return buffered.length;
  }

  /**
   * Discard the buffer and return to idle. A no-op unless armed — aborting a
   * live turn is stopRecording's job, not the gate's.
   *
   * @returns {boolean} whether there was an armed capture to abort
   */
  abort() {
    if (this.state !== GATE_ARMED) return false;
    this.reset();
    return true;
  }

  /**
   * Force idle from any state, discarding the buffer. For start-failure
   * cleanup, where a commit went live but the worklet never started (so no
   * final chunk will ever arrive to close the gate).
   */
  reset() {
    this.state = GATE_IDLE;
    this._buffer = [];
  }

  /**
   * Route one worklet audio message.
   * @returns {"emitted"|"buffered"|"dropped"}
   */
  onAudioChunk(msg, emitChunk) {
    if (this.state === GATE_LIVE) {
      emitChunk(msg);
      return "emitted";
    }
    if (this.state === GATE_ARMED) {
      if (this._buffer.length >= MAX_BUFFERED_CHUNKS) {
        this._buffer.shift();
        this.droppedWhileArmed++;
      }
      this._buffer.push(msg);
      return "buffered";
    }
    return "dropped";
  }

  /**
   * The worklet reported its final chunk after a stop. Only honoured in
   * "live": an aborted arm produces one too, and emitting USER_AUDIO_DONE for
   * it would finalize a turn that was never opened.
   *
   * @returns {boolean} true if USER_AUDIO_DONE should be emitted.
   */
  onFinalChunk() {
    if (this.state === GATE_LIVE) {
      this.state = GATE_IDLE;
      return true;
    }
    return false;
  }

  get bufferedCount() {
    return this._buffer.length;
  }
}
